/**
 * App.js - Main application with routing
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Signup from './components/Signup';
import Dashboard from './components/Dashboard';
import ScheduleForm from './components/ScheduleForm';
import PublicLeaguePage from './components/PublicLeaguePage';
import AdminPage from './components/AdminPage';
import ScoreEntry from './components/ScoreEntry';
import PlayoffSetup from './components/PlayoffSetup';
import ContactForm from './components/ContactForm';
import PricingPage from './components/PricingPage';
import CaptainPortal from './components/CaptainPortal';
import RefereePortal from './components/RefereePortal';
import ResetPasswordPage from './components/ResetPasswordPage';
import EmbedSchedule from './components/EmbedSchedule';
import NavBar from './components/NavBar';
import Settings from './components/Settings';
import LeagueViewPage from './components/LeagueViewPage';
import CaptainsPage from './components/CaptainsPage';
import VenuesPage from './components/VenuesPage';
import RefereesPage from './components/RefereesPage';
import GameManagementPage from './components/GameManagementPage';
import RegistrationFormBuilder from './components/RegistrationFormBuilder';
import RosterManagementPage from './components/RosterManagementPage';
import LeagueWebsitePage from './components/LeagueWebsitePage';
import PublicLeagueWebsite from './components/PublicLeagueWebsite';
import PublicRegistrationPage from './components/PublicRegistrationPage';
import RegistrationSuccessPage from './components/RegistrationSuccessPage';
import ForcePasswordChange from './components/ForcePasswordChange';
import HelpPage from './components/HelpPage';
import FAQPage from './components/FAQPage';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import BlogListPage from './components/BlogListPage';
import BlogArticleDetail from './components/BlogArticleDetail';
import TournamentRegistrationPage from './components/TournamentRegistrationPage';
import PublicTournamentPage from './components/PublicTournamentPage';
import TournamentsPage from './components/TournamentsPage';
import TournamentAdminPage from './components/TournamentAdminPage';
import PickleballRatingsPage from './components/PickleballRatingsPage';
import NotFoundPage from './components/NotFoundPage';
import ErrorBoundary from './components/ErrorBoundary';
import Logo from './components/Logo';
import { getPlanDisplayName } from './utils/planNames';
import { loadSportConfigsFromAPI } from './utils/sportConfig';
import './App.css';

// ---------------------------------------------------------------------------
// Hostname-based routing detection (evaluated once at module load)
// When the React SPA is served from a custom subdomain (e.g. myteam.leaguemaker.app)
// or a custom domain (e.g. springfieldsoftball.com), render the league website
// directly instead of the full authenticated app.
// ---------------------------------------------------------------------------
const _LEAGUEMAKER_DOMAIN = 'leaguemaker.app';
const _APP_HOSTNAMES = [
  _LEAGUEMAKER_DOMAIN,
  `www.${_LEAGUEMAKER_DOMAIN}`,
  `dev.${_LEAGUEMAKER_DOMAIN}`,
];
const CURRENT_HOSTNAME = window.location.hostname;
const _isLocalhost = CURRENT_HOSTNAME === 'localhost' || CURRENT_HOSTNAME === '127.0.0.1';
const _isSubdomain =
  !_isLocalhost &&
  !_APP_HOSTNAMES.includes(CURRENT_HOSTNAME) &&
  CURRENT_HOSTNAME.endsWith(`.${_LEAGUEMAKER_DOMAIN}`);
const _isCustomDomain =
  !_isLocalhost && !CURRENT_HOSTNAME.includes(_LEAGUEMAKER_DOMAIN);
const HOSTNAME_MODE = _isSubdomain || _isCustomDomain;

function AppContent() {
  const { isAuthenticated, user, loading, logout, refreshUser, previewTier, clearPreviewTier } = useAuth();
  const { colors } = useTheme();
  // 'landing' | 'login' | 'signup'
  const [authView, setAuthView] = useState('landing');
  const [showForm, setShowForm] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showPlayoffs, setShowPlayoffs] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLeagueView, setShowLeagueView] = useState(false);
  const [showCaptains, setShowCaptains] = useState(false);
  const [showVenues, setShowVenues] = useState(false);
  const [showReferees, setShowReferees] = useState(false);
  const [showGameManagement, setShowGameManagement] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [showWebsite, setShowWebsite] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showTournaments, setShowTournaments] = useState(false);
  const [showTournamentAdmin, setShowTournamentAdmin] = useState(false);
  const [showPickleballRatings, setShowPickleballRatings] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedLeagueName, setSelectedLeagueName] = useState('');

  // Load sport configs from API on mount
  useEffect(() => {
    loadSportConfigsFromAPI();
  }, []);

  // Handle Stripe Connect return/refresh redirects
  const [pendingStripeReturn] = useState(() => {
    const path = window.location.pathname;
    return path === '/settings/stripe/return' || path === '/settings/stripe/refresh';
  });

  useEffect(() => {
    if (pendingStripeReturn && isAuthenticated && !loading) {
      window.history.replaceState({}, '', '/');
      navigateToDashboard();
      setShowSettings(true);
    }
  }, [pendingStripeReturn, isAuthenticated, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Override body background for authenticated pages (theme-aware)
  useEffect(() => {
    if (isAuthenticated) {
      document.body.style.background = colors.bg;
      document.documentElement.style.background = colors.bg;
    }
    return () => {
      document.body.style.background = '';
      document.documentElement.style.background = '';
    };
  }, [isAuthenticated, colors.bg]);

  const navigateToDashboard = () => {
    setShowForm(false);
    setShowAdmin(false);
    setShowScores(false);
    setShowContact(false);
    setShowPlayoffs(false);
    setShowSettings(false);
    setShowLeagueView(false);
    setShowCaptains(false);
    setShowVenues(false);
    setShowReferees(false);
    setShowGameManagement(false);
    setShowRegistration(false);
    setShowRoster(false);
    setShowWebsite(false);
    setShowHelp(false);
    setShowTournaments(false);
    setShowTournamentAdmin(false);
    setShowPickleballRatings(false);
    setSelectedTournament(null);
    setSelectedLeague(null);
    setSelectedLeagueName('');
  };

  const handleLogout = () => {
    logout();
    setAuthView('landing');
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <h1><Logo size="2.5rem" /></h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public routes - no auth required */}
        <Route path="/public/:slug" element={<PublicLeaguePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/captain/:token" element={<CaptainPortal />} />
        <Route path="/referee/:token" element={<RefereePortal />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/embed/:slug" element={<EmbedSchedule />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogArticleDetail />} />
        <Route path="/join/t/:joinSlug" element={<TournamentRegistrationPage />} />
        <Route path="/t/:joinSlug" element={<PublicTournamentPage />} />
        <Route path="/register/:leagueId" element={<PublicRegistrationPage />} />
        <Route path="/register/:leagueId/success" element={<RegistrationSuccessPage />} />
        <Route path="/league/:identifier" element={<PublicLeagueWebsite />} />
        <Route path="/404" element={<NotFoundPage />} />

        {/* All other routes */}
        <Route
          path="/*"
          element={
            isAuthenticated && user?.mustChangePassword ? (
              <ForcePasswordChange
                onPasswordChanged={() => refreshUser()}
                onLogout={handleLogout}
              />
            ) : isAuthenticated ? (
              <>
                {previewTier && (
                  <div className="admin-preview-banner">
                    <span>Admin Preview Mode: Viewing as <strong>{getPlanDisplayName(previewTier)}</strong> tier</span>
                    <button onClick={clearPreviewTier} className="admin-preview-banner-exit">Exit Preview</button>
                  </div>
                )}
                <NavBar
                  user={user}
                  onHome={navigateToDashboard}
                  onAdmin={user?.isAdmin ? () => {
                    navigateToDashboard();
                    setShowAdmin(true);
                  } : null}
                  onSettings={() => {
                    navigateToDashboard();
                    setShowSettings(true);
                  }}
                  onHelp={() => {
                    navigateToDashboard();
                    setShowHelp(true);
                  }}
                  onContact={() => {
                    navigateToDashboard();
                    setShowContact(true);
                  }}
                  onCaptains={() => {
                    navigateToDashboard();
                    setShowCaptains(true);
                  }}
                  onVenues={user?.plan !== 'free' ? () => {
                    navigateToDashboard();
                    setShowVenues(true);
                  } : null}
                  onReferees={user?.plan === 'pro' ? () => {
                    navigateToDashboard();
                    setShowReferees(true);
                  } : null}
                  onTournaments={() => {
                    navigateToDashboard();
                    setShowTournaments(true);
                  }}
                  onLogout={handleLogout}
                />
                <div className="app-body">
                  {showHelp ? (
                    <HelpPage onBack={() => setShowHelp(false)} />
                  ) : showSettings ? (
                    <Settings onBack={() => setShowSettings(false)} />
                  ) : showContact ? (
                    <ContactForm onBack={navigateToDashboard} />
                  ) : showPlayoffs ? (
                    <PlayoffSetup
                      leagueId={selectedLeague}
                      onBack={() => {
                        setShowPlayoffs(false);
                        setSelectedLeague(null);
                      }}
                    />
                  ) : showScores ? (
                    <ScoreEntry
                      leagueId={selectedLeague}
                      onBack={() => {
                        setShowScores(false);
                        setSelectedLeague(null);
                      }}
                    />
                  ) : showCaptains ? (
                    <CaptainsPage onBack={() => setShowCaptains(false)} />
                  ) : showVenues ? (
                    <VenuesPage onBack={() => setShowVenues(false)} />
                  ) : showReferees ? (
                    <RefereesPage onBack={() => setShowReferees(false)} />
                  ) : showAdmin ? (
                    <AdminPage onBack={() => setShowAdmin(false)} />
                  ) : showLeagueView ? (
                    <LeagueViewPage
                      leagueId={selectedLeague}
                      onBack={() => {
                        setShowLeagueView(false);
                        setSelectedLeague(null);
                      }}
                      onEditSchedule={(id) => {
                        setShowLeagueView(false);
                        setSelectedLeague(id);
                        setShowForm(true);
                      }}
                      onEditScores={(id) => {
                        setShowLeagueView(false);
                        setSelectedLeague(id);
                        setShowScores(true);
                      }}
                      onEditPlayoffs={(id) => {
                        setShowLeagueView(false);
                        setSelectedLeague(id);
                        setShowPlayoffs(true);
                      }}
                      onGameManagement={(id) => {
                        setShowLeagueView(false);
                        setSelectedLeague(id);
                        setShowGameManagement(true);
                      }}
                    />
                  ) : showGameManagement ? (
                    <GameManagementPage
                      leagueId={selectedLeague}
                      onBack={() => {
                        setShowGameManagement(false);
                        setSelectedLeague(null);
                      }}
                    />
                  ) : showRegistration ? (
                    <RegistrationFormBuilder
                      leagueId={selectedLeague}
                      onBack={() => {
                        setShowRegistration(false);
                        setSelectedLeague(null);
                      }}
                    />
                  ) : showRoster ? (
                    <RosterManagementPage
                      leagueId={selectedLeague}
                      onBack={() => {
                        setShowRoster(false);
                        setSelectedLeague(null);
                      }}
                    />
                  ) : showTournamentAdmin ? (
                    <TournamentAdminPage
                      tournamentId={selectedTournament}
                      onBack={() => {
                        setShowTournamentAdmin(false);
                        setSelectedTournament(null);
                        setShowTournaments(true);
                      }}
                    />
                  ) : showTournaments ? (
                    <TournamentsPage
                      onBack={() => setShowTournaments(false)}
                      onManage={(id) => {
                        setSelectedTournament(id);
                        setShowTournaments(false);
                        setShowTournamentAdmin(true);
                      }}
                    />
                  ) : showWebsite ? (
                    <LeagueWebsitePage
                      leagueId={selectedLeague}
                      leagueName={selectedLeagueName}
                      onBack={() => {
                        setShowWebsite(false);
                        setSelectedLeague(null);
                        setSelectedLeagueName('');
                      }}
                    />
                  ) : showPickleballRatings ? (
                    <PickleballRatingsPage
                      leagueId={selectedLeague}
                      leagueName={selectedLeagueName}
                      onBack={() => {
                        setShowPickleballRatings(false);
                        setSelectedLeague(null);
                        setSelectedLeagueName('');
                      }}
                    />
                  ) : showForm ? (
                    <ScheduleForm
                      onBack={() => {
                        setShowForm(false);
                        setSelectedLeague(null);
                      }}
                      leagueId={selectedLeague}
                    />
                  ) : (
                    <Dashboard
                      onCreateNew={() => setShowForm(true)}
                      onViewLeague={(leagueId) => {
                        setSelectedLeague(leagueId);
                        setShowLeagueView(true);
                      }}
                      onPlayoffs={(leagueId) => {
                        setSelectedLeague(leagueId);
                        setShowPlayoffs(true);
                      }}
                      onGameManagement={(leagueId) => {
                        setSelectedLeague(leagueId);
                        setShowGameManagement(true);
                      }}
                      onRegistration={(leagueId) => {
                        setSelectedLeague(leagueId);
                        setShowRegistration(true);
                      }}
                      onRoster={(leagueId) => {
                        setSelectedLeague(leagueId);
                        setShowRoster(true);
                      }}
                      onWebsite={(leagueId, leagueName) => {
                        setSelectedLeague(leagueId);
                        setSelectedLeagueName(leagueName || '');
                        setShowWebsite(true);
                      }}
                      onPickleballRatings={(leagueId, leagueName) => {
                        setSelectedLeague(leagueId);
                        setSelectedLeagueName(leagueName || '');
                        setShowPickleballRatings(true);
                      }}
                    />
                  )}
                </div>
              </>
            ) : authView === 'login' ? (
              <Login
                onSwitchToSignup={() => setAuthView('signup')}
                onBackToHome={() => setAuthView('landing')}
              />
            ) : authView === 'signup' ? (
              <Signup
                onSwitchToLogin={() => setAuthView('login')}
                onBackToHome={() => setAuthView('landing')}
              />
            ) : (
              <LandingPage
                onLogin={() => setAuthView('login')}
                onSignup={() => setAuthView('signup')}
              />
            )
          }
        />
      </Routes>
    </Router>
  );
}

function App() {
  // Short-circuit for custom subdomains (myteam.leaguemaker.app) and custom
  // domains (springfieldsoftball.com). Serve only the league website — no
  // authenticated app shell, no auth providers needed.
  if (HOSTNAME_MODE) {
    return (
      <ErrorBoundary>
        <Router>
          <Routes>
            {/* Pass registration sub-routes through so links within the league site work */}
            <Route path="/register/:leagueId" element={<PublicRegistrationPage />} />
            <Route path="/register/:leagueId/success" element={<RegistrationSuccessPage />} />
            {/* Everything else resolves to the league website for this hostname */}
            <Route path="/*" element={<PublicLeagueWebsite hostname={CURRENT_HOSTNAME} />} />
          </Routes>
        </Router>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
